import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, MapPin, Globe2 } from 'lucide-react';

/**
 * CityCarousel — horizontal scrollable list of cities the user can filter to.
 * Props:
 *   cities: [{ name, count, country }]
 *   active: string ('all' for "All cities")
 *   onChange: (city|null) => void
 */
const CityCarousel = ({ cities = [], active = 'all', onChange }) => {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  if (!cities.length) return null;

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <section className="relative" data-testid="city-carousel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{t('home.shop_by_city', 'Shop by city')}</h2>
          <span className="text-xs text-slate-400">{cities.length} {t('home.cities', 'cities')}</span>
        </div>
        <div className="hidden sm:flex gap-1">
          <button onClick={() => scroll(-1)} aria-label="scroll left"
            className="h-8 w-8 rounded-full border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center" data-testid="city-scroll-left">
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <button onClick={() => scroll(1)} aria-label="scroll right"
            className="h-8 w-8 rounded-full border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center" data-testid="city-scroll-right">
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>
      </div>

      <div ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth"
        style={{ scrollSnapType: 'x mandatory' }}>
        {/* All cities pill */}
        <button onClick={() => onChange?.('all')} data-testid="city-card-all"
          style={{ scrollSnapAlign: 'start' }}
          className={`shrink-0 w-44 rounded-2xl border-2 p-4 text-left transition-all ${active === 'all' ? 'border-emerald-500 bg-emerald-50/40 -translate-y-0.5 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300 hover:-translate-y-0.5'}`}>
          <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-2">
            <Globe2 className="h-4 w-4" />
          </div>
          <div className="font-semibold text-slate-900 leading-tight">{t('home.all_cities', 'All cities')}</div>
          <div className="text-xs text-slate-500 mt-0.5">{cities.reduce((s, c) => s + (c.count || 0), 0)} {t('home.stores', 'stores')}</div>
        </button>

        {cities.map((c) => {
          const on = active === c.name;
          return (
            <button key={c.name} onClick={() => onChange?.(c.name)} data-testid={`city-card-${c.name.toLowerCase()}`}
              style={{ scrollSnapAlign: 'start' }}
              className={`shrink-0 w-44 rounded-2xl border-2 p-4 text-left transition-all ${on ? 'border-emerald-500 bg-emerald-50/40 -translate-y-0.5 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300 hover:-translate-y-0.5'}`}>
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center mb-2">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="font-semibold text-slate-900 leading-tight truncate">{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{c.country}</div>
              <div className="text-[11px] mt-2 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                {c.count} {t('home.stores', 'stores')}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default CityCarousel;
